import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Calendar, Sprout, TrendingUp, Sun, AlertTriangle, Target } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Section = ({ icon: Icon, title, items, color, emptyMsg, renderItem }) => (
  <div className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm">
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <h2 className="text-xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>
    </div>
    {items && items.length > 0 ? (
      <div className="space-y-2">{items.map(renderItem)}</div>
    ) : (
      <p className="text-sm text-[#5C665C]">{emptyMsg}</p>
    )}
  </div>
);

function WeeklyPlan({ user }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/today/weekly-plan`, { withCredentials: true });
        setPlan(res.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <Layout user={user}><div className="flex items-center justify-center h-96"><div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div></div></Layout>;

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div>
          <p className="text-sm text-[#5C665C] font-medium uppercase tracking-wider">Sunday Garden Plan</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Week of {plan?.week_starting}
          </h1>
          <p className="text-base text-[#5C665C] mt-2 capitalize">{plan?.season} · {plan?.active_crops} active crop{plan?.active_crops !== 1 ? 's' : ''}</p>
        </div>

        {/* Skill Focus Hero */}
        <div className="bg-gradient-to-br from-[#1A4331] to-[#255C44] rounded-3xl p-8 text-white" data-testid="skill-focus-hero">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Target className="w-6 h-6" />
            </div>
            <div>
              <p className="text-xs font-bold tracking-wider uppercase opacity-80">This week&apos;s skill focus</p>
              <h2 className="text-2xl font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{plan?.skill_focus}</h2>
              <p className="text-sm opacity-90 mt-3 leading-relaxed">
                Small focused practice each week builds real grower skill over the season.
              </p>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Section
            icon={Sprout} title="Sow this week"
            items={plan?.to_sow} color="bg-[#B4D330]/10 text-[#1A4331]"
            emptyMsg="No new crops to sow this month."
            renderItem={item => (
              <Link key={item.crop_id} to={`/crops/${item.crop_id}`} data-testid={`sow-${item.crop_id}`} className="block p-3 bg-[#F0F2EB] hover:bg-[#B4D330]/10 rounded-xl transition-colors">
                <p className="font-medium text-[#1A4331]">{item.name}</p>
                <p className="text-xs text-[#5C665C] capitalize">{item.difficulty}</p>
              </Link>
            )}
          />

          <Section
            icon={Sprout} title="Transplant"
            items={plan?.to_transplant} color="bg-[#8EAA97]/10 text-[#1A4331]"
            emptyMsg="No transplanting needed this month."
            renderItem={item => (
              <div key={item.crop_id} data-testid={`tp-${item.crop_id}`} className="p-3 bg-[#F0F2EB] rounded-xl">
                <p className="font-medium text-[#1A4331]">{item.name}</p>
              </div>
            )}
          />

          <Section
            icon={TrendingUp} title="Harvest"
            items={plan?.to_harvest} color="bg-[#D4AF37]/10 text-[#D4AF37]"
            emptyMsg="Nothing ready to harvest yet."
            renderItem={item => (
              <div key={item.planting_id} data-testid={`harvest-${item.planting_id}`} className="p-3 bg-[#D4AF37]/10 rounded-xl">
                <p className="font-medium text-[#1A4331]">{item.crop_name}</p>
              </div>
            )}
          />

          <Section
            icon={Sun} title="Feed"
            items={plan?.to_feed} color="bg-[#E2725B]/10 text-[#E2725B]"
            emptyMsg="No heavy feeders need attention this week."
            renderItem={(item, i) => (
              <div key={i} data-testid={`feed-${i}`} className="p-3 bg-[#F0F2EB] rounded-xl">
                <p className="font-medium text-[#1A4331]">{item.crop_name}</p>
                <p className="text-xs text-[#5C665C]">Add compost or liquid feed</p>
              </div>
            )}
          />
        </div>

        {/* Pests to Watch */}
        {plan?.pests_to_watch && plan.pests_to_watch.length > 0 && (
          <div className="bg-[#E2725B]/5 border border-[#E2725B]/20 rounded-3xl p-6" data-testid="pests-to-watch">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="w-6 h-6 text-[#E2725B]" />
              <h2 className="text-xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Watch for this week</h2>
            </div>
            <ul className="space-y-2">
              {plan.pests_to_watch.map((pest, i) => (
                <li key={i} className="flex items-start gap-2 text-[#2D241E]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E2725B] mt-2"></span>
                  <span>{pest}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default WeeklyPlan;
